<template>
    <div data-backdrop="static" data-keyboard="false" class="modal fade" id="carrier-area-form-modal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title page-title" v-html="modalTitle"/>
                </div>
                <form @submit.prevent="submit" class="form-horizontal">
                    <div class="modal-body">
                        <div class="form-group row">
                            <label for="carrier-area-name" class="col-12 control-label">
                                <span v-html="$t('CarrierAreaFormModal.name')"/>
                                <RedAsterisk/>
                            </label>
                            <div class="col-12">
                                <input id="carrier-area-name" :placeholder="$t('CarrierAreaFormModal.required')" class="form-control"
                                       required ref="nameInput" v-model="carrierArea.name"/>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label for="carrier-area-enabledForApi" class="col-6 control-label" v-html="$t('CarrierAreaFormModal.enabledForApi')"/>
                            <label for="carrier-area-margin" class="col-6 control-label">
                                <span v-html="$t('CarrierAreaFormModal.margin')"/>
                                <InfoCircle :tooltip="$t('CarrierAreaFormModal.whatIsMargin')"/>
                            </label>
                            <div class="col-6">
                                <toggle-button :value="carrierArea.enabledForApi" :sync="true" v-model="carrierArea.enabledForApi"/>
                            </div>
                            <div class="col-6">
                                <div class="input-group">
                                    <input id="carrier-area-margin" min="0" max="100"
                                           :placeholder="$t('CarrierAreaFormModal.optional')" type="number"
                                           class="form-control" step="0.01" v-model="carrierArea.margin"/>
                                    <div class="input-group-text">
                                        <span class="input-group-append" v-html="'%'"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label for="associated-countries" class="col-12 control-label" v-html="$t('CarrierAreaFormModal.associatedCountries')"/>
                            <div class="col-12" @keydown.enter.prevent="">
                                <VSelect id="associated-countries" :placeholder="$t('CarrierAreaFormModal.chooseAssociatedCountries')"
                                          @input="setSelectedAssociatedDestinations" multiple :filter-by="filterBy"
                                          :value="selectedAssociatedDestinations" :options="formattedAssociatedDestinations">
                                    <template slot="option" slot-scope="option">
                                        <img class="mr-1" alt="flag" width="15" :src="option.flagURL"/>
                                        <span v-html="option.label"/>
                                    </template>
                                </VSelect>
                            </div>
                        </div>
                        <div v-if="newSelectedAssociatedDestinations" class="row">
                            <div class="col-12 p-3">
                                <div v-html="$t('CarrierAreaFormModal.destinationsWillTakeAreaPrices')" class="alert alert-info"/>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer flex justify-content-between">
                        <button :disabled="loading" type="button" class="btn btn-secondary" data-dismiss="modal" v-html="$t('CarrierAreaFormModal.close')"/>
                        <SubmitBtn classes="btn btn-primary" :text="modalMainAction" :loading="loading"/>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import CarrierArea from "../../../../classes/Billing/Carrier/CarrierArea";
import SubmitBtn from "../../forms/SubmitBtn";
import RedAsterisk from "../../forms/RedAsterisk";
import InfoCircle from "@/components/shared/misc/InfoCircle";
import { filterBy } from "../../../../helpers/VSelect";

export default {
    name: "CarrierAreaFormModal",
    components: {RedAsterisk, SubmitBtn, InfoCircle},
    props: {
        carrier: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            carrierArea: new CarrierArea(),
            selectedAssociatedDestinations: [],
            mode: "create",
        };
    },
    methods: {
        show(carrierArea = null) {
            this.mode = carrierArea === null ? "create" : "edit";
            this.carrierArea = new CarrierArea(carrierArea);
            this.selectedAssociatedDestinations = this.formattedAssociatedDestinations.filter(destination => destination.area.id !== undefined && destination.area.id === this.carrierArea.id);
            if (this.mode === "create") {
                setTimeout(() => this.$refs.nameInput.focus(), 500);
            }
            // eslint-disable-next-line no-undef
            $("#carrier-area-form-modal").modal("show");
        },
        async submit() {
            this.carrierArea.carrierId = this.carrier.id;
            this.carrierArea.sanitizeForAPI();
            try {
                this.loading = true;
                if (this.mode === "create") {
                    await this.submitCreate();
                } else if (this.mode === "edit") {
                    await this.submitEdit();
                }
            } catch (e) {
                this.$displayError(e);
            } finally {
                this.loading = false;
            }
        },
        async submitCreate() {
            let response = await this.$diceAPI.postCarrierArea(this.carrierArea);
            await this.associateDestinationsToArea(response.data.id);
            response = await this.$diceAPI.getCarrierArea(response.data.id);
            this.$emit("addCarrierArea", response.data);
            // eslint-disable-next-line no-undef
            $("#carrier-area-form-modal").modal("hide");
            this.$toasted.success(this.$t("CarrierAreaFormModal.carrierAreaAdded"), { icon: "check" });
        },
        async submitEdit() {
            let response = await this.$diceAPI.patchCarrierArea(this.carrierArea);
            await this.associateDestinationsToArea(this.carrierArea.id);
            response = await this.$diceAPI.getCarrierArea(this.carrierArea.id);
            this.$emit("editCarrierArea", response.data);
            // eslint-disable-next-line no-undef
            $("#carrier-area-form-modal").modal("hide");
            this.$toasted.success(this.$t("CarrierAreaFormModal.carrierAreaEdited"), { icon: "check" });
        },
        setSelectedAssociatedDestinations(associatedDestinations) {
            this.selectedAssociatedDestinations = associatedDestinations;
        },
        async associateDestinationsToArea(areaId) {
            const promises = [];
            const disassociatedDestinations = this.carrierArea.destinations.filter(destination => this.selectedAssociatedDestinations.find(d => d.key === destination.id) === undefined);
            for (const destination of disassociatedDestinations) {
                promises.push(this.$diceAPI.deleteCarrierAreaDestination(areaId, destination.id));
            }
            for (const destination of this.selectedAssociatedDestinations) {
                promises.push(this.$diceAPI.addCarrierAreaDestination(areaId, destination.key));
            }
            await Promise.all(promises);
        },
        filterBy,
    },
    computed: {
        modalTitle() {
            return this.mode === "create" ? this.$t("CarrierAreaFormModal.addCarrierArea") : this.$t("CarrierAreaFormModal.editCarrierArea");
        },
        modalMainAction() {
            return this.mode === "create" ? this.$t("CarrierAreaFormModal.add") : this.$t("CarrierAreaFormModal.edit");
        },
        formattedAssociatedDestinations() {
            return this.carrier.destinations.map(destination => ({
                key: destination.id,
                label: `${destination.country.name}`,
                area: destination.area,
                flagURL: this.$diceAPI.getBaseUrl() + destination.country.flagURL,
            }));
        },
        newSelectedAssociatedDestinations() {
            return this.selectedAssociatedDestinations.filter(destination => this.carrierArea.destinations.find(d => d.id === destination.key) === undefined).length;
        },
        getDiceBaseUrl() {
            return this.$diceAPI.getBaseUrl();
        },
    },
};
</script>

<style scoped>

</style>